/**
 * Shared right-side slide-over shell (portal).
 * Used by Cap-raise Emails message drawer and Campaign recipients drawer.
 */
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon } from '@heroicons/react/24/outline';

export interface SlideOverPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  children: React.ReactNode;
  /**
   * Panel max-width. Default max-w-2xl for email + campaign recipients drawers.
   * Investor detail uses its own shell (Area 5) — do not assume this default there.
   */
  widthClassName?: string;
  /** Aria label for the backdrop close control */
  closeBackdropLabel?: string;
  /** Pinned under the scrolling body. Campaign follow-up no longer uses this. */
  footer?: React.ReactNode;
  /** Sits under the title, still outside the scrolling body. Used for drawer tabs. */
  headerBottom?: React.ReactNode;
}

const SlideOverPanel: React.FC<SlideOverPanelProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  widthClassName = 'max-w-2xl',
  closeBackdropLabel = 'Close panel',
  footer,
  headerBottom,
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      // Compose and other Headless UI dialogs sit above this panel. Esc closes only that dialog.
      if (document.querySelector('[data-headlessui-state="open"]')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const panel = (
    <div className="fixed inset-0 z-[99] flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 cursor-default bg-black/50"
        aria-label={closeBackdropLabel}
        onClick={onClose}
      />
      <div
        className={`relative z-[100] flex h-full w-full ${widthClassName} flex-col bg-white shadow-2xl`}
      >
        <div className="shrink-0 overflow-visible border-b border-primary-500/10">
        <div className="flex items-start justify-between gap-3 px-3 py-3 sm:px-4 sm:py-3">
          <div className="min-w-0 flex-1">
            {typeof title === 'string' ? (
              <h3 className="truncate text-base font-semibold text-primary-800">{title}</h3>
            ) : (
              title
            )}
            {subtitle ? (
              typeof subtitle === 'string' ? (
                <p className="mt-0.5 truncate text-sm text-primary-700">{subtitle}</p>
              ) : (
                <div className="mt-0.5 min-w-0">{subtitle}</div>
              )
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 min-w-11 shrink-0 touch-manipulation items-center justify-center rounded-md text-primary-500/70 hover:bg-primary-50 hover:text-primary-700 sm:min-h-8 sm:min-w-8"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {headerBottom}
        </div>
        <div
          className={`min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4 sm:py-3 ${
            footer ? '' : 'pb-[max(1rem,env(safe-area-inset-bottom))]'
          }`}
        >
          {children}
        </div>
        {footer ? (
          <div className="shrink-0 border-t border-primary-500/10 bg-white px-3 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:px-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
};

export default SlideOverPanel;
